﻿import {defaultFetch, httpMethod} from "@/scripts/Common/api";
import type {ApiResponse} from "@/scripts/Common/api";
import type {AppConfigurations, Customer, FetchCustomersParam, Service} from "@/components/customers/scripts/types";
import { getToken} from "@/scripts/Common";
import type { ImportResult } from "@/scripts/Common";
import { cleanBankAccountForApi } from "@/scripts/Common/helpers/apiTransformations";
import i18n from "@/i18n";

const fetchCustomers = async (args: FetchCustomersParam = {}, abortSignal? : AbortSignal): Promise<ApiResponse<Customer[]>> => {

  const {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  } = await defaultFetch({
    apiName: 'customersV2',
    bearer: getToken(),
    options: {
      query: {
        ...args,
        pageNumber: typeof args?.pageNumber === 'number' ? args.pageNumber - 1 : undefined, // reduce by 1 to make it 0 based
      },
      abortSignal
    }
  });

  return {
    response,
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount
  }
}

const fetchCustomer = async (id: number, abortSignal? : AbortSignal): Promise<ApiResponse<Customer>> => {

  // validate arguments
  if (id <= 0){
    throw new Error('Id must be greater than 0.');
  }

  const {response,
    error,
  } = await defaultFetch({
    apiName: 'customerResource',
    bearer: getToken(),
    options: {
      routeParams: {
        customerId: id.toString()
      },
      abortSignal
    }
  });

  return {
    response: response as Customer,
    error,
  }
}

const fetchCustomerDeviceAuthConfiguration = async (customerId: number, deviceId: number): Promise<ApiResponse<AppConfigurations>> => await defaultFetch({
    apiName: 'customderDeviceAuthConfiguration',
    options: {
        routeParams: {
            customerId: customerId.toString(10),
            deviceId: deviceId.toString(10)
        }
    },
    bearer: getToken()
});

const fetchCustomerServices = async (): Promise<ApiResponse<Service[]>> => await defaultFetch({
    apiName: 'service',
    bearer: getToken()
});


const fetchDefaultProvision = async (): Promise<ApiResponse<number>> => await defaultFetch({
    apiName: 'defaultProvision',
    bearer: getToken()
});


const deleteCustomer = async (customerId: number): Promise<{ error: any }> => {
    const {
        error,
    } = await defaultFetch({
        apiName: 'customerResource',
        options: {
            method: httpMethod.DELETE,
            routeParams: {
                customerId: customerId.toString(10)
            }
        },
        bearer: getToken()
    });

    return {
        error,
    };
}

/**
 * Cleans up customer object by converting null values to empty strings for API compatibility
 * @param customer - The customer object to clean
 * @returns Cleaned customer object
 */
const cleanCustomerForApi = (customer: Customer): Customer => {
    return {
        ...customer,
        businessBankAccount: cleanBankAccountForApi(customer.businessBankAccount),
    };
};

const createOrUpdateCustomer = async (customer: Customer, method: httpMethod.PUT | httpMethod.POST)
    : Promise<ApiResponse<number>> => {

    const cleanedCustomer = cleanCustomerForApi(customer);

    return await defaultFetch({
        apiName: 'customers',
        options: {
            method
        },
        body: JSON.stringify(cleanedCustomer),
        bearer: getToken()
    });
};


const updateCustomerState = async (customer: Customer, action: 'contract' | 'confirm' | 'cancel')
    : Promise<ApiResponse<number>> => {

    const cleanedCustomer = cleanCustomerForApi(customer);

    return await defaultFetch({
        apiName: 'customerStateChange',
        options: {
            method: httpMethod.PUT,
            routeParams: {
                action
            }
        },
        body: JSON.stringify(cleanedCustomer),
        bearer: getToken()
    });
};



const importCustomers = async (file: File) => {
  const {
    error,
    response,
  } = await defaultFetch({
    apiName: 'importCustomers',
    bearer: getToken(),
    options: {
      method: httpMethod.PUT,
      headers: {
        'Content-Type': 'text/csv',
        'Authorization': `Bearer ${getToken()}`,
        'Accept-Language': i18n.global.locale.value
      }
    },
    body: file,
  });

  return {
    error,
    response: response as ImportResult
  }
}



export {
    fetchCustomers,
    fetchCustomer,
    fetchCustomerDeviceAuthConfiguration,
    deleteCustomer,
    createOrUpdateCustomer,
    fetchCustomerServices,
    fetchDefaultProvision,
    updateCustomerState,
    importCustomers
}
